class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;
      if (this.#activeFetch || this.#swapInProgress) return;

      if (this.dataset.recommendationsPerformed === 'true') continue;

      this.#loadRecommendations();
      break;
    }
  });

  #cachedRecommendations = {};
  #activeFetch = null;
  #swapInProgress = false;

  #disableAutoReload() {
    this.#mutationObserver.disconnect();
    this.dataset.recommendationsPerformed = 'true';
  }

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) return;
    if (recommendationsPerformed === 'true') return;

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then(async (result) => {
        if (!result.success) {
          if (typeof Shopify === 'undefined' || !Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = result.data || '';
        const newRecsContainer = tempDiv.querySelector(`product-recommendations[id="${id}"]`);

        if (newRecsContainer && newRecsContainer.innerHTML.trim().length) {

          const newContent = newRecsContainer.querySelector('flickity-carousel') || newRecsContainer.firstElementChild;

          const currentContent = Array.from(this.children).find(child =>
            !child.hasAttribute('data-is-swapping')
          );

          if (newContent && currentContent) {
            this.#crossfadeSwap(currentContent, newContent);
          } else {
            this.innerHTML = newRecsContainer.innerHTML;
            this.dataset.recommendationsPerformed = 'true';
          }
        } else {
          this.#disableAutoReload();
        }
      })
      .catch((e) => {
        if (e.name === 'AbortError') return;
        this.#handleError(e);
      });
  }

  #crossfadeSwap(oldEl, newEl) {
    if (this.#swapInProgress) {
      this.innerHTML = '';
      this.appendChild(newEl);
      this.dataset.recommendationsPerformed = 'true';
      this.#swapInProgress = false;
      return;
    }

    this.#swapInProgress = true;
    newEl.setAttribute('data-is-swapping', 'true');

    this.style.display = 'grid';
    this.style.gridTemplateAreas = '"stack"';
    this.style.gridTemplateColumns = 'minmax(0, 1fr)';

    oldEl.style.gridArea = 'stack';
    oldEl.style.zIndex = '1';
    oldEl.style.width = '100%';

    newEl.style.gridArea = 'stack';
    newEl.style.zIndex = '2';
    newEl.style.opacity = '0';
    newEl.style.width = '100%';
    newEl.style.transition = 'opacity 0.6s ease-out';

    this.appendChild(newEl);
    const safetyTimeout = setTimeout(() => {
      finishSwap();
    }, 1500);

    let attempts = 0;
    const checkInit = setInterval(() => {
      attempts++;
      const flkty = newEl.flickityInstance;
      if ((flkty && flkty.cells && flkty.cells.length > 0) || attempts > 25) {
        clearInterval(checkInit);
        clearTimeout(safetyTimeout);

        if (flkty) {
          flkty.resize();
          flkty.reposition();
        }

        setTimeout(() => {
          requestAnimationFrame(() => {
            newEl.style.opacity = '1';
            if (oldEl) {
              oldEl.style.transition = 'opacity 0.6s ease-out';
              oldEl.style.opacity = '0';
            }

            setTimeout(finishSwap, 650);
          });
        }, 50);
      }
    }, 50);

    const finishSwap = () => {
      if (oldEl && oldEl.parentNode) {
        oldEl.remove();
      }

      newEl.removeAttribute('data-is-swapping');
      newEl.style.gridArea = '';
      newEl.style.zIndex = '';
      newEl.style.opacity = '';
      newEl.style.transition = '';
      newEl.style.width = '';

      this.style.display = '';
      this.style.gridTemplateAreas = '';
      this.style.gridTemplateColumns = '';
      const flkty = newEl.flickityInstance;
      if (flkty) {
        flkty.resize();
      }

      this.dataset.recommendationsPerformed = 'true';
      this.#swapInProgress = false;

      const carousels = this.querySelectorAll('flickity-carousel');
      if (carousels.length > 1) {
        for (let i = 0; i < carousels.length - 1; i++) {
          carousels[i].remove();
        }
      }
    };
  }

  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  #handleError(error) {
    console.error('Product recommendations error:', error);
    this.#disableAutoReload();
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}